import '@leanpay/ui/main.css'

import React, { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import ReactDOM from 'react-dom/client'
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router-dom'

import { initPdfDownloadHandler } from '@leanpay/services'
import { Environment, redactQueryParamsDeep } from '@leanpay/utils'

import App from 'app/app'
import { currentEnvironment, currentMarket } from 'library/constants'

import './index.css'

import { initI18n } from './i18n'

// Session replays only on prod — the small monthly replay quota must not be spent by test/stage runs.
const isProdEnvironment = currentEnvironment === Environment.PROD
// Errors are only actionable from real deployments — local/tilt runs must not spend the event quota.
const isSentryEnabled =
    isProdEnvironment ||
    currentEnvironment === Environment.STAGE ||
    currentEnvironment === Environment.TEST

// The credit app is opened with these in the URL. The handoff code stays usable until the exchange
// completes, and Sentry copies URLs into requests, breadcrumbs and spans.
const SENSITIVE_URL_PARAMS = ['handoffCode', 'token']

Sentry.init({
    dsn: 'https://1050bc8301f3ee9f2ae5c1a6b22dfdd6@o4508023659560960.ingest.de.sentry.io/4511687634649168',
    integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        ...(isProdEnvironment ? [Sentry.replayIntegration()] : []),
    ],
    enabled: isSentryEnabled,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: isProdEnvironment ? 1.0 : 0,
    environment: currentEnvironment.toLowerCase(),
    beforeSend: (event) => redactQueryParamsDeep(event, SENSITIVE_URL_PARAMS),
    beforeSendTransaction: (event) => redactQueryParamsDeep(event, SENSITIVE_URL_PARAMS),
    ignoreErrors: [
        'Non-Error promise rejection captured with value: 502',
        'Failed to fetch dynamically imported module',
        'Network Error',
        'Fetch request to',
        'The object can not be cloned',
    ],
})
Sentry.setTag('market', currentMarket.toLowerCase())

initPdfDownloadHandler()

// Initialize i18n before rendering
initI18n().then(() => {
    ReactDOM.createRoot(document.getElementById('root')!).render(
        <React.StrictMode>
            <App />
        </React.StrictMode>,
    )
})
